"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildSeoSchema = buildSeoSchema;
const createSchemaObject = (type, obj) => ({
    '@type': type,
    ...obj,
});
/**
 * Recursively concatenates text from the children of an accordion item.
 */
const concatChildText = (children) => {
    let textPieces = [];
    children.forEach((child) => {
        // Extract text from text nodes
        if (child.type === 'text' &&
            'text' in child &&
            typeof child.text === 'string') {
            textPieces.push(child.text);
        }
        else if (child.type === 'inlineBlock' &&
            'fields' in child &&
            child.fields &&
            typeof child.fields === 'object' &&
            'BlockReference' in child.fields) {
            const link = child.fields.BlockReference;
            if (link && typeof link === 'object')
                textPieces.push(link.text || '');
        }
        // Recursively process children if they exist
        if (child.children && Array.isArray(child.children)) {
            const childText = concatChildText(child.children);
            if (childText)
                textPieces.push(childText);
        }
    });
    return textPieces.join(' ');
};
/**
 * Generates an array of stringified JSON from an array of objects with SEO data.
 * NOTE: This function does not return the whole array stringified because it needs to be mapped over
 * to generate each json object inside a script tag for current usage.
 */
function buildSeoSchema({ alternateName, siteName, siteURL, title, description, schemaType, mainEntity, customSchema, override, packages, }) {
    const schemaObjects = [
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteName,
            alternateName,
            url: siteURL,
        },
    ];
    const appendCustomSchema = () => {
        if (Array.isArray(customSchema) && customSchema.length > 0) {
            customSchema.forEach((customSchemaObj) => {
                if (!customSchemaObj || typeof customSchemaObj !== 'object')
                    return;
                schemaObjects.push(customSchemaObj);
            });
        }
    };
    /*
     *  If the override option is set/true, add the custom schema to
     * the starting schema. This does not override the starting schema,
     * but it does override the rest of the page schema that comes from,
     * contentful.
     */
    if (override) {
        appendCustomSchema();
        return schemaObjects.map((schemaObj) => JSON.stringify(schemaObj, null, 2));
    }
    const pageSchema = {
        '@context': 'https://schema.org',
        ...createSchemaObject(schemaType || 'WebPage', {
            name: title,
            description,
        }),
    };
    let entitySchema;
    if (mainEntity && typeof mainEntity.value === 'object') {
        if (mainEntity.relationTo === 'packageCards' && mainEntity.value.brandy) {
            const brandy = packages[mainEntity.value.brandy] || null;
            if (brandy && brandy.name && brandy.price?.amount) {
                entitySchema = createSchemaObject('Product', {
                    name: brandy.name,
                    offers: createSchemaObject('Offer', {
                        priceCurrency: 'USD',
                        price: brandy.price.amount,
                    }),
                });
            }
        }
        else if (mainEntity.relationTo === 'accordions' &&
            mainEntity.value.items?.length) {
            const { identifier, items } = mainEntity.value;
            switch (schemaType) {
                case 'FAQPage':
                    entitySchema = items?.map((item) => createSchemaObject('Question', {
                        name: item.title,
                        acceptedAnswer: createSchemaObject('Answer', {
                            text: item?.mainContent
                                ? concatChildText(item.mainContent.root.children)
                                : '',
                        }),
                    }));
                    break;
                default:
                    entitySchema = createSchemaObject('ItemList', {
                        name: identifier,
                        itemListElement: items.map((item, index) => createSchemaObject('ListItem', {
                            position: index + 1,
                            item: createSchemaObject('Thing', {
                                name: item.title,
                            }),
                        })),
                    });
            }
        }
    }
    schemaObjects.push({
        ...pageSchema,
        mainEntity: entitySchema,
    });
    appendCustomSchema();
    return schemaObjects.map((schemaObj) => JSON.stringify(schemaObj, null, 2));
}
